import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Notification } from '../../components/Notification';
import { NOTIFICATION_URL } from '../../utils/apiEndpoints';
import clientService from '../../apiClient';
import { genWorkspaceHeader } from '../../utils/constants';

const initialState = {
  notifications: [],
  loading: false,
  initialLoading: false,
  loadingMore: false,
  error: null,
  hasMore: true,
  page: 0,
  limit: 5,
};
export const fetchNotifications = createAsyncThunk(
  'notification/fetchnotifications',
  async ({ skip, limit }, { rejectWithValue }) => {
    try {
      const response = await clientService.get(`${NOTIFICATION_URL}`, {
        params: { skip, limit },
      });
      return { data: response.data, skip, limit };
    } catch (err) {
      console.log(err);
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);
export const markNotificationAsRead = createAsyncThunk(
  'notification/marknotificationasread',
  async (id, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${NOTIFICATION_URL}/${id}`, { is_read: true });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notification/deletenotification',
  async (id, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${NOTIFICATION_URL}/${id}`, { is_delete: true });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const markAllNotificationAsRead = createAsyncThunk(
  'notification/markallnotificationasread',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const unreadIds = state.notification.notifications
        .filter((notification) => !notification.is_read)
        .map((notification) => notification.id);

      if (unreadIds.length === 0) {
        return { data: [] }; // No unread notifications to mark
      }

      const response = await clientService.post(`${NOTIFICATION_URL}`, { notification_ids: unreadIds, is_read: true });
      return response.data;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);
export const addWatcher = createAsyncThunk(
  'notification/addwatcher',
  async ({ fileId, workspace_id, userId }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(
        `${NOTIFICATION_URL}/watcher`,
        {
          action: 'add',
          resource: fileId,
          watchers: [userId],
          entity_name: 'DOCUMENT',
        },
        { headers: genWorkspaceHeader(workspace_id) }
      );
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);

export const removeWatcher = createAsyncThunk(
  'notification/removewatcher',
  async ({ fileId, workspace_id, userId }, { rejectWithValue }) => {
    try {
      const response = await clientService.put(`${NOTIFICATION_URL}/watcher`, {
        action: 'delete',
        resource: fileId,
        watchers: [userId],
        entity_name: 'DOCUMENT',
      });
      return response;
    } catch (err) {
      return rejectWithValue({ error: err?.data?.error?.message });
    }
  }
);
export const notificationSlice = createSlice({
  name: 'notification',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(fetchNotifications.pending, (state, action) => {
        // Check if this is the initial load or loading more
        if (action.meta.arg.skip === 0) {
          state.initialLoading = true;
        } else {
          state.loadingMore = true;
        }
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        // Reset appropriate loading state
        if (action.meta.arg.skip === 0) {
          state.initialLoading = false;
          state.notifications = action.payload.data;
        } else {
          state.loadingMore = false;
          state.notifications = [...state.notifications, ...action.payload.data];
        }
        state.hasMore = action.payload.data.length === action.payload.limit;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        // Reset both loading states on error
        state.initialLoading = false;
        state.loadingMore = false;
        state.error = action.payload.error;
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const updatedNotification = action.payload.data;
        const index = state.notifications.findIndex((n) => n.id === updatedNotification.id);
        if (index !== -1) {
          state.notifications[index] = updatedNotification;
        }
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const deletedNotification = action.payload.data;
        state.notifications = state.notifications.filter((n) => n.id !== deletedNotification.id);
      })
      .addCase(markAllNotificationAsRead.fulfilled, (state, action) => {
        state.notifications = state.notifications.map((n) => ({ ...n, is_read: true }));
      });
  },
});

export default notificationSlice.reducer;
